import { isAfter } from "date-fns";
import { formatNumber, getCleanStartorEndDay, replaceComma } from "helpers/utils";
import type { DTOTermPeriodData } from "store/managementStore";
import type { TPeriod } from "store/periodStore";
import type { TermPeriodDataImpactSummary } from "store/termStore";
import { PeriodStatus } from "store/types";

export type SliceWithAmount = { amount?: string | number | null };

export type SliceWithAmountAndDate = SliceWithAmount & {
    paymentDate?: Date | string | null;
};

/** Fields needed to mirror `getTotalAdvancesInPeriod` slice eligibility. */
export type SliceWithTpdAdvanceEligibility = SliceWithAmountAndDate & {
    legacyImpactBalanceN1TpdId?: number | null;
    isForfait?: number | null;
};

/**
 * Standalone advances that count toward TPD `advanceAmount` (excludes legacy balance N-1 path).
 * @see AdvancePaymentRepository.getTotalAdvancesInPeriod
 */
export function isSliceEligibleForTpdAdvanceAmount(slice: SliceWithTpdAdvanceEligibility): boolean {
    if (slice.isForfait === 1) return false;
    return slice.legacyImpactBalanceN1TpdId == null;
}

export function sumEligibleSliceAmountCentsInPeriod(
    slices: SliceWithTpdAdvanceEligibility[],
    periodStart: Date,
    periodEnd: Date,
): number {
    return slices.reduce((sum, slice) => {
        if (!isSliceEligibleForTpdAdvanceAmount(slice)) return sum;
        if (!isSlicePaymentDateInPeriod(slice.paymentDate, periodStart, periodEnd)) return sum;
        return sum + sliceAmountToCents(slice.amount);
    }, 0);
}

export type TpdLike = DTOTermPeriodData | TermPeriodDataImpactSummary | null | undefined;

/** Sums slice amounts entered in euros and returns total in cents. */
export function sumSliceAmountCents(slices: SliceWithAmount[]): number {
    return slices.reduce((sum, slice) => sum + sliceAmountToCents(slice.amount), 0);
}

function sliceAmountToCents(amount: string | number | null | undefined): number {
    const parsed = Number(replaceComma(String(amount ?? 0)));
    if (Number.isNaN(parsed)) return 0;
    return Math.round(parsed * 100);
}

export type ManagementPreviewContext = {
    period?: TPeriod;
    tpd?: DTOTermPeriodData;
};

/** Active term_period_data row for the contract (open period). */
export function resolveContractOpenTpd(termTpds: DTOTermPeriodData[]): DTOTermPeriodData | undefined {
    const activeTpd = termTpds.find((row) => row.isActive);
    if (activeTpd) return activeTpd;
    if (termTpds.length === 0) return undefined;
    return termTpds.reduce((latest, row) => (row.id > latest.id ? row : latest));
}

/** Resolves period + TPD for the management table preview: always the contract's open period. */
export function resolveManagementPreviewContext(
    periods: TPeriod[],
    termTpds: DTOTermPeriodData[],
): ManagementPreviewContext {
    const tpd = resolveContractOpenTpd(termTpds);
    const period = tpd ? periods.find((row) => row.id === tpd.periodId) : undefined;
    return { period, tpd };
}

export type SlicePaymentDateImpactKind = "open" | "future" | "none";

function parseSlicePaymentDate(paymentDate: Date | string | null | undefined): Date | undefined {
    if (paymentDate == null || paymentDate === "") return undefined;
    const date = paymentDate instanceof Date ? paymentDate : getCleanStartorEndDay(paymentDate);
    if (Number.isNaN(date.getTime())) return undefined;
    return date;
}

export type AdvanceSlicePaymentDateValidationMessages = {
    invalidDate: string;
    beforePeriodStart: string;
};

/** React-hook-form validate callback for advance slice payment dates. */
export function validateAdvanceSlicePaymentDate(
    value: Date | string | null | undefined,
    minPaymentDate: Date | undefined,
    messages: AdvanceSlicePaymentDateValidationMessages,
): string | undefined {
    if ((value as unknown as string) === "Invalid Date") {
        return messages.invalidDate;
    }

    if (minPaymentDate && value) {
        const date = getCleanStartorEndDay(value as Date, { time: "start" });
        if (date < minPaymentDate) {
            return messages.beforePeriodStart;
        }
    }

    return undefined;
}

/**
 * Whether a slice payment date will impact gross duty on an open or upcoming period.
 * Closed periods and missing dates return "none".
 */
export function resolveSlicePaymentDateImpactKind(
    paymentDate: Date | string | null | undefined,
    periods: Pick<TPeriod, "periodStart" | "periodEnd" | "isActive">[],
    activePeriod?: Pick<TPeriod, "periodEnd" | "isActive"> | null,
): SlicePaymentDateImpactKind {
    const payment = parseSlicePaymentDate(paymentDate);
    if (!payment) return "none";

    const isInPeriodKind = (kind: PeriodStatus) =>
        periods
            .filter((period) => period.isActive === kind)
            .some((period) => isSlicePaymentDateInPeriod(payment, period.periodStart, period.periodEnd));

    if (isInPeriodKind(PeriodStatus.CURRENTLY_OPEN)) return "open";
    if (isInPeriodKind(PeriodStatus.FUTURE)) return "future";

    if (activePeriod?.periodEnd) {
        const periodEnd = getCleanStartorEndDay(activePeriod.periodEnd);
        if (!Number.isNaN(periodEnd.getTime()) && isAfter(payment, periodEnd)) {
            return "future";
        }
    }

    return "none";
}

/** Same rules as AdvancePaymentRepository.getTotalAdvancesInPeriod (paymentDate within period). */
export function isSlicePaymentDateInPeriod(
    paymentDate: Date | string | null | undefined,
    periodStart: Date,
    periodEnd: Date,
): boolean {
    if (paymentDate == null) return false;
    const date = paymentDate instanceof Date ? paymentDate : getCleanStartorEndDay(paymentDate);
    const start = getCleanStartorEndDay(periodStart);
    const end = getCleanStartorEndDay(periodEnd, { time: "end" });
    return date >= start && date <= end;
}

export function sumSliceAmountCentsInPeriod(
    slices: SliceWithAmountAndDate[],
    periodStart: Date,
    periodEnd: Date,
): number {
    return slices.reduce((sum, slice) => {
        if (!isSlicePaymentDateInPeriod(slice.paymentDate, periodStart, periodEnd)) return sum;
        return sum + sliceAmountToCents(slice.amount);
    }, 0);
}

export function getTpdAdvanceAmountCents(tpd: TpdLike): number {
    return Number((tpd as TermPeriodDataImpactSummary & { advanceAmount?: number })?.advanceAmount ?? 0);
}

export function formatManagementAdvanceAmount(cents: number): string {
    return formatNumber(-Math.round(cents) / 100, { isCurrency: true, always2Digit: false });
}

export function formatManagementAmount(cents: number): string {
    return formatNumber(Math.round(cents) / 100, { isCurrency: true, always2Digit: false });
}

/** Resolves preview advance total: form slices take precedence over TPD data when provided. */
export function resolvePreviewAdvanceAmountCents(tpd: TpdLike, previewAdvanceAmountCents?: number): number {
    if (previewAdvanceAmountCents != null) {
        return previewAdvanceAmountCents;
    }
    return getTpdAdvanceAmountCents(tpd);
}
